import { createApi } from "@reduxjs/toolkit/dist/query/react";
import { baseQueryWithProvider } from "@/redux/baseQuery";
import { createSearchParams } from "@/utils";
import { transformNetwork, transformNetworkHistory, transformNetworkItem } from "./helpers";
import {
  GetNetworkResponse,
  GetNetworkParams,
  RawNetwork,
  GetNetworkHistoryResponse,
  GetNetworkHistoryParams,
  GetNetworkItemResponse,
} from "./types";

export const networkApi = createApi({
  reducerPath: "networkApi",
  baseQuery: baseQueryWithProvider("network"),
  endpoints: (builder) => ({
    getNetwork: builder.query<GetNetworkResponse, GetNetworkParams>({
      query: (params) => `?${createSearchParams(params).toString()}`,
      providesTags: ["Network"],
      transformResponse: transformNetwork,
    }),
    approveInvitation: builder.mutation<RawNetwork, string>({
      query: (id) => ({
        url: `/${id}`,
        method: "PATCH",
        body: { status: "approve" },
      }),
    }),
    rejectInvitation: builder.mutation<RawNetwork, string>({
      query: (id) => ({
        url: `/${id}`,
        method: "PATCH",
        body: { status: "reject" },
      }),
    }),
    inviteNetwork: builder.mutation<RawNetwork, string>({
      query: (providerCode) => ({
        url: "",
        method: "POST",
        body: { provider_code: providerCode },
      }),
      invalidatesTags: ["Network"],
    }),
    removeNetwork: builder.mutation<void, string>({
      query: (id) => ({
        url: `/${id}`,
        method: "DELETE",
      }),
    }),
    getNetworkHistory: builder.query<GetNetworkHistoryResponse, GetNetworkHistoryParams>({
      query: ({ id, page, pageSize }) => {
        let queryString = `/${id}/history?`;
        if (page) queryString = `${queryString}&page=${page}`;
        if (pageSize) queryString = `${queryString}&page_size=${pageSize}`;
        return queryString;
      },
      transformResponse: transformNetworkHistory,
    }),
    getNetworkItem: builder.query<GetNetworkItemResponse, string>({
      query: (id) => `/${id}`,
      transformResponse: transformNetworkItem,
    }),
  }),
  tagTypes: ["Network"],
});

export const {
  useGetNetworkQuery,
  useInviteNetworkMutation,
  useRemoveNetworkMutation,
  useApproveInvitationMutation,
  useRejectInvitationMutation,
  useLazyGetNetworkHistoryQuery,
  useLazyGetNetworkItemQuery,
} = networkApi;
